import { Fragment, useMemo, useState } from 'react';
import {
  Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text,
} from '@tremor/react';
import { t } from '../lib/i18n';
import { usePagination } from '../lib/usePagination';
import Pagination from './Pagination';
import type { LineCard, VendorScorecard } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0,
});
const fmtPct = new Intl.NumberFormat('es-MX', {
  style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1,
});

interface Props {
  lineCards: LineCard[];
  vendors: VendorScorecard[];
  showCost: boolean;
}

interface PerLineGroup {
  line: string;
  cards: LineCard[];
  spend: number;
}

export default function VendorLineTable({ lineCards, vendors, showCost }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const spendByVendor = useMemo(() => {
    const m = new Map<string, number>();
    for (const v of vendors) m.set(`${v.sid}-${v.sup_id}`, v.total_ordered_mxn ?? 0);
    return m;
  }, [vendors]);

  const fillByVendor = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const v of vendors) m.set(`${v.sid}-${v.sup_id}`, v.fill_rate);
    return m;
  }, [vendors]);

  const grouped = useMemo<PerLineGroup[]>(() => {
    const byLine = new Map<string, LineCard[]>();
    for (const c of lineCards) {
      const arr = byLine.get(c.line) ?? [];
      arr.push(c);
      byLine.set(c.line, arr);
    }
    const groups: PerLineGroup[] = [];
    for (const [line, cards] of byLine) {
      let spend = 0;
      for (const c of cards) spend += spendByVendor.get(`${c.sid}-${c.sup_id}`) ?? 0;
      groups.push({ line, cards, spend });
    }
    groups.sort((a, b) => b.spend - a.spend);
    return groups;
  }, [lineCards, spendByVendor]);
  const pg = usePagination(grouped);

  if (lineCards.length === 0) {
    return <Card><Text>{t.vendors.emptyLineCards}</Text></Card>;
  }

  const toggle = (line: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(line)) next.delete(line);
      else next.add(line);
      return next;
    });
  };

  return (
    <Card className="p-0 overflow-hidden">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t.vendors.cols.line}</TableHeaderCell>
            <TableHeaderCell className="text-right">{t.vendors.cols.supplierCount}</TableHeaderCell>
            {showCost && (
              <TableHeaderCell className="text-right">{t.vendors.cols.spend180d}</TableHeaderCell>
            )}
            <TableHeaderCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {pg.pageRows.map((g) => (
            <Fragment key={g.line}>
              <TableRow
                className="cursor-pointer hover:bg-tremor-background-muted"
                onClick={() => toggle(g.line)}
              >
                <TableCell className="font-mono">{g.line}</TableCell>
                <TableCell className="text-right">{g.cards.length}</TableCell>
                {showCost && (
                  <TableCell className="text-right">{fmtMxn.format(g.spend)}</TableCell>
                )}
                <TableCell className="text-right text-tremor-content text-xs">
                  {expanded.has(g.line) ? '▾' : '▸'}
                </TableCell>
              </TableRow>
              {expanded.has(g.line) && g.cards
                .slice()
                .sort((a, b) => (spendByVendor.get(`${b.sid}-${b.sup_id}`) ?? 0) - (spendByVendor.get(`${a.sid}-${a.sup_id}`) ?? 0))
                .map((c) => {
                  const key = `${c.sid}-${c.sup_id}`;
                  const spend = spendByVendor.get(key) ?? 0;
                  const fr = fillByVendor.get(key) ?? null;
                  return (
                    <TableRow key={`${g.line}-${key}`} className="bg-tremor-background-subtle text-xs">
                      <TableCell className="pl-8">
                        <span className="font-mono text-tremor-content mr-2">{c.sup_id}</span>
                        {c.name}
                        <span className="ml-3 text-tremor-content">{t.vendors.cols.lev} {c.lev}</span>
                        {showCost && c.disc !== undefined && c.disc > 0 && (
                          <span className="ml-2 text-tremor-content">{t.vendors.cols.disc} {fmtPct.format(c.disc)}</span>
                        )}
                      </TableCell>
                      <TableCell />
                      {showCost && (
                        <TableCell className="text-right">{fmtMxn.format(spend)}</TableCell>
                      )}
                      <TableCell className="text-right">
                        {fr == null ? <span className="text-tremor-content">—</span> : fmtPct.format(fr)}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </Fragment>
          ))}
        </TableBody>
      </Table>
      <Pagination {...pg} className="px-4" />
    </Card>
  );
}
